import { ChevronRight } from 'lucide-react';
import { SCENARIO_LABELS } from 'Modules/softphone/components/SoftphoneDevTools/constants';
import { DevToolsMicroLabel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsMicroLabel';
import type { DebugScenarioSummaryProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugScenarioSummary/index.types';
import type { DevToolsTone } from 'Modules/softphone/components/SoftphoneDevTools/index.types';
import {
  SURFACE,
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import type { TransferOutcome } from 'Modules/softphone/services/DebugService/index.types';
import React from 'react';
import { cn } from '~/lib/utils';

const OUTCOME_INK: Record<string, string> = {
  accepted: 'text-emerald-400',
  failed: 'text-red-400',
  cancelled: 'text-zinc-300',
  referred: 'text-amber-400',
};

/**
 * The card takes the transfer's outcome as its tone, so an observed success
 * reads green and a failure red. Without a transfer (a conference) it keeps
 * the warning tone.
 */
const OUTCOME_TONE: Record<TransferOutcome, DevToolsTone> = {
  accepted: 'ok',
  failed: 'bad',
  cancelled: 'muted',
  referred: 'warn',
};

/**
 * DebugScenarioSummary - Scenario, relationships and outcome for a captured leg
 *
 * Two things this component exists to say out loud:
 */
export const DebugScenarioSummary: React.FC<DebugScenarioSummaryProps> = ({
  info,
  onOpenLeg,
  availableLegIds = [],
}) => {
  const hasScenario = Boolean(info.scenario && info.scenario !== 'direct');

  if (!hasScenario && !info.transfer && !info.conference) {
    return null;
  }

  const relatedCallIds = info.relatedCallIds ?? [];
  const tone =
    TONE[info.transfer ? OUTCOME_TONE[info.transfer.outcome] : 'warn'];

  return (
    <div className={tone.card}>
      <div className={cn('flex items-center gap-2', tone.header)}>
        {hasScenario && (
          <span
            className={cn(
              'text-[10px] font-bold uppercase tracking-[0.07em]',
              tone.text
            )}
          >
            {SCENARIO_LABELS[info.scenario as string] ?? info.scenario}
          </span>
        )}
        <div className="flex-1" />
        {info.endedBy && (
          <span className={cn('font-sp-mono text-[10.5px]', tone.text)}>
            ended by {info.endedBy}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2.5 p-3">
        {info.transfer && (
          <>
            <div className="flex gap-2.5">
              <div className="min-w-0 flex-1">
                <DevToolsMicroLabel>Destination</DevToolsMicroLabel>
                <div className="mt-0.5 truncate font-sp-mono text-[12.5px] text-zinc-200">
                  {info.transfer.destination ?? 'N/A'}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <DevToolsMicroLabel>Outcome</DevToolsMicroLabel>
                <div className="mt-0.5 flex items-center gap-1.5">
                  <span
                    className={cn(
                      'font-sp-mono text-[12.5px]',
                      OUTCOME_INK[info.transfer.outcome] ?? 'text-zinc-200'
                    )}
                  >
                    {info.transfer.outcome}
                  </span>
                  {info.transfer.outcome === 'referred' && (
                    <span className="rounded border border-amber-800 bg-amber-950 px-[5px] py-px text-[9.5px] font-semibold text-amber-400">
                      NOT SUCCESS
                    </span>
                  )}
                </div>
              </div>
            </div>

            {info.transfer.outcome === 'referred' && (
              <p
                className={cn(
                  SURFACE.inset,
                  'px-2.5 py-2.5 text-[11.5px] leading-[1.55] text-zinc-300'
                )}
              >
                The REFER was sent and the far end reported nothing back. Only a
                NOTIFY carrying a 2xx upgrades this to{' '}
                <span className="font-sp-mono text-emerald-400">accepted</span>.
                Success was attempted, not observed.
              </p>
            )}

            {info.transfer.failureReason && (
              <p
                className={cn(
                  TONE.bad.inset,
                  'px-2.5 py-2.5 text-[11.5px] leading-[1.55] text-red-400'
                )}
              >
                {info.transfer.failureReason}
              </p>
            )}

            {info.transfer.referResponses &&
              info.transfer.referResponses.length > 0 && (
                <div>
                  <DevToolsMicroLabel>REFER responses</DevToolsMicroLabel>
                  <div className="mt-0.5 font-sp-mono text-[11px] text-zinc-300">
                    {info.transfer.referResponses.join(' · ')}
                  </div>
                </div>
              )}
          </>
        )}

        {info.conference && (
          <>
            <div className="flex gap-2.5">
              <div className="min-w-0 flex-1">
                <DevToolsMicroLabel>Room</DevToolsMicroLabel>
                <div className="mt-0.5 truncate font-sp-mono text-[12.5px] text-zinc-200">
                  {info.conference.roomGuid}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <DevToolsMicroLabel>Participants</DevToolsMicroLabel>
                <div className="mt-0.5 font-sp-mono text-[12.5px] text-zinc-200">
                  {info.conference.participantIds?.length ?? 0}
                </div>
              </div>
            </div>

            <p
              className={cn(
                SURFACE.inset,
                'px-2.5 py-2.5 text-[11.5px] leading-[1.55] text-zinc-300'
              )}
            >
              Participants are tracked locally by the client — the conference
              server emits no join/leave events, so this list is inferred.
            </p>

            {info.conference.rolledBack && (
              <p
                className={cn(
                  TONE.bad.inset,
                  'px-2.5 py-2.5 text-[11.5px] text-red-400'
                )}
              >
                This merge was rolled back.
              </p>
            )}
          </>
        )}

        {relatedCallIds.length > 0 && (
          <div>
            <DevToolsMicroLabel className="mb-[5px]">
              Other legs of this interaction
            </DevToolsMicroLabel>
            <div className="flex flex-col gap-1">
              {relatedCallIds.map((id) => {
                // A related id can outlive the record it points at, once the
                // 50-record cap has dropped the sibling. Linking to nothing is
                // worse than printing the id, so only reachable legs are links.
                const isReachable =
                  Boolean(onOpenLeg) && availableLegIds.includes(id);

                if (!isReachable) {
                  return (
                    <div
                      key={id}
                      className={cn(
                        SURFACE.inset,
                        'truncate px-2.5 py-2 font-sp-mono text-[11px] text-zinc-400'
                      )}
                      title="This leg is no longer in the history"
                    >
                      {id}
                    </div>
                  );
                }

                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onOpenLeg?.(id)}
                    className={cn(
                      SURFACE.inset,
                      'flex w-full items-center gap-2 px-2.5 py-2 text-left transition-colors',
                      'hover:border-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40'
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate font-sp-mono text-[11px] text-zinc-300">
                      {id}
                    </span>
                    <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
